import React from "react";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router-dom";

import { SubmitButton } from "@limitless/customer/components";
import Typography from "@mui/material/Typography";

import { useCustomerContext, useCustomerMessageContext } from "@limitless/customer/hooks";
import Grid from "@limitless/modern/layout/Grid";
import CodeCollector from "@limitless/modern/inputs/code-collector/CodeCollector";
import { useTranslation } from "@limitless/common/hooks/translation/TranslationContext";

import Anchor from "./Anchor";

const useStyles = makeStyles(theme => ({
    container: {
        padding: "14px",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 6px -2px rgba(0, 0, 0, 0.06)",
        borderRadius: "16px"
    },
    heading: {
        fontSize: "14px",
        textAlign: "center"
    },
    reContactMessageCreationError: {
        color: theme.palette.error.dark,
        textAlign: "center"
    },
    flexCenter: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: theme.spacing(1.5)
    },
    gridColumnCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
    },
    gridRowCenter: {
        direction: "row",
        justifyContent: "center",
        alignItems: "center"
    }
}));

const MessageVerificationForm = () => {
    const { customer, getCustomerData, updateCustomer } = useCustomerContext();
    const { createMessage, reContactMessageCreationError, message, generateVerificationCode, setDialogOpen } =
        useCustomerMessageContext();
    const [disabled, setDisabled] = React.useState(false);
    const [clearCode, setClearCodes] = React.useState(false);
    const history = useHistory();
    const classes = useStyles();
    const { t } = useTranslation();

    const { email, verificationCode } = customer;

    const handleAnchorClick = () => {
        const payload = {
            customerFirstName: customer?.name?.value,
            email: customer?.email?.value
        };
        generateVerificationCode(payload, message?.groupName);
    };

    React.useEffect(() => {
        if (reContactMessageCreationError) {
            setClearCodes(true);
            updateCustomer("verificationCode", "");
        }
    }, [reContactMessageCreationError]);

    React.useEffect(() => {
        setDisabled(!verificationCode?.value);
    }, [verificationCode]);

    const handleSubmit = () => {
        const customerData = getCustomerData();
        createMessage({ ...customerData, enforce: true }, newMsg => {
            if (newMsg) {
                updateCustomer("verificationCode", "");
                setDialogOpen(false);
                history.push(`/conversation/${newMsg.messageId}/${newMsg.accessKey}/${false}`);
            }
        });
    };

    const isCodeValid = verificationCode?.value?.length !== 4;

    return (
        <div className={classes.container}>
            <Typography variant="caption" classes={{ caption: classes.heading }} component="div">
                {t("In order to continue, please enter the verification code that has been sent to ")}
                <Typography
                    component="span"
                    style={{
                        color: "rgba(0, 0, 0, 0.87)",
                        fontWeight: 700,
                        letterSpacing: "0.25px"
                    }}
                >
                    {email.value}{" "}
                </Typography>
                {t("below:")}
            </Typography>

            <Grid
                mt={theme => theme.spacing(1.5)}
                mb={theme => theme.spacing(1.5)}
                className={classes.gridColumnCenter}
            >
                <CodeCollector
                    max={4}
                    onUpdate={code => updateCustomer("verificationCode", code)}
                    clearAll={clearCode}
                    onError={reContactMessageCreationError}
                    isRecontactConfig={true}
                />
            </Grid>
            <div className={classes.flexCenter}>
                <SubmitButton
                    disabled={isCodeValid || disabled || message.loading}
                    title="Verify"
                    width={116}
                    handleSubmit={handleSubmit}
                    loading={message.loading}
                />
            </div>

            <Grid mt={theme => theme.spacing(1.5)} container className={classes.gridRowCenter}>
                {!reContactMessageCreationError ? (
                    <Typography variant="caption" color="textPrimary" textAlign="center">
                        <i>{t("Didn’t get the code?")}</i>{" "}
                        {t("Check your spam folder. If you still haven't received it, request a new one")}{" "}
                        <Anchor text={t("here.")} onClick={handleAnchorClick} />
                    </Typography>
                ) : (
                    <>
                        <Typography variant="caption" className={classes.reContactMessageCreationError}>
                            {t("The verification code is not valid.")}
                        </Typography>
                        <Typography variant="caption" className={classes.reContactMessageCreationError}>
                            {t("Please try again or ")}{" "}
                            <Anchor text={t("send a new code")} onClick={handleAnchorClick} />
                        </Typography>
                    </>
                )}
            </Grid>
        </div>
    );
};

export default MessageVerificationForm;
